import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { ErrorMessage } from '../../../../../shared/models/error-message/error-message';
import { CheckIconComponent } from '../../../icons/check-icon/check-icon.component';
import { ErrorMessageTableSearchComponent } from '../../../search/error-message/error-message-table-search/error-message-table-search.component';
import { RouterModule } from '@angular/router';
import { DecisionModalComponent } from '../../../modals/decision-modal/decision-modal.component';
import { DeleteIconComponent } from '../../../icons/delete-icon/delete-icon.component';
import { ErrorMessageQuery } from '../../../../../shared/models/error-messages/error-message-query';
import { CommonModule } from '@angular/common';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { Policy } from '../../../../../shared/models/info/policy';
import { Decision } from '../../../../../shared/models/internal/decision';
import { CommonUtils, getPropertyName } from '../../../../common/common-utils';
import { SortingConfiguration } from '../../../../../shared/internal/sorting-configuration';
import { HeaderSorterComponent } from '../../../common/tables/header-sorter/header-sorter.component';
import { NoteIconComponent } from '../../../icons/note-icon/note-icon.component';
import { RestoreIconComponent } from '../../../icons/restore-icon/restore-icon.component';
import { ScrollableBase } from '../../../base/scrollable-base';

@Component({
  selector: 'app-error-message-table',
  imports: [
    CheckIconComponent,
    ErrorMessageTableSearchComponent,
    RouterModule,
    DecisionModalComponent,
    DeleteIconComponent,
    CommonModule,
    EnabledByPolicyDirective,
    AgentActionDirective,
    HeaderSorterComponent,
    NoteIconComponent,
    RestoreIconComponent,
  ],
  templateUrl: './error-message-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './error-message-table.component.scss',
})
export class ErrorMessageTableComponent extends ScrollableBase<ErrorMessageQuery> {
  @Input() public errorMessages?: ErrorMessage[] = [];
  @Input() public softDeleteDisabled = false;
  @Input() public restoreDisabled = false;
  @Input() public hardDeleteDisabled = false;
  @Input() public selectedElementId?: string;
  @Input() public recyclingModeEnabled = false;
  @Output() public searchTriggered: EventEmitter<ErrorMessageQuery> = new EventEmitter<ErrorMessageQuery>();
  @Output() public softDeleteErrorMessage: EventEmitter<ErrorMessage> = new EventEmitter<ErrorMessage>();
  @Output() public hardDeleteErrorMessage: EventEmitter<ErrorMessage> = new EventEmitter<ErrorMessage>();
  @Output() public errorMessageClicked: EventEmitter<string> = new EventEmitter<string>();
  @Output() public sortByFieldChanged: EventEmitter<ErrorMessageQuery> = new EventEmitter<ErrorMessageQuery>();
  @Output() public restoreErrorMessageTriggered: EventEmitter<ErrorMessage> = new EventEmitter<ErrorMessage>();

  public errorMessageToRemove?: ErrorMessage;
  public policy = Policy;
  public commonUtils = CommonUtils;
  public getPropertyName = getPropertyName<ErrorMessage>;
  public sortingConfiguration = new SortingConfiguration(this.getPropertyName('code'));

  public onSearchTriggered(query: ErrorMessageQuery): void {
    this.query = query;
    const sortByField = this.sortingConfiguration.SortByField;
    if (sortByField) {
      this.query.orderBy = sortByField.field;
      this.query.orderByDescending = sortByField.orderByDescending;
    }
    this.searchTriggered.emit(query);
  }

  public onSoftDeleteDecision(decision: Decision): void {
    if (decision.result) {
      this.softDeleteErrorMessage.emit(this.errorMessageToRemove);
    }
  }

  public onHardDeleteDecision(decision: Decision): void {
    if (decision.result) {
      this.hardDeleteErrorMessage.emit(this.errorMessageToRemove);
    }
  }

  public showDecisionModal(id?: string, header?: string): void {
    this.errorMessageToRemove = { id: id, headerOnTerminal: header, dtcIds: [] };
  }

  public onErrorMessageClicked(id?: string): void {
    this.errorMessageClicked.emit(id);
  }

  public restoreErrorMessage(em: ErrorMessage): void {
    this.restoreErrorMessageTriggered.emit(em);
  }

  public sortBy(field: string): void {
    const sortByField = this.sortingConfiguration.sortBy(field);
    if (this.query) {
      this.query.orderBy = sortByField.field;
      this.query.orderByDescending = sortByField.orderByDescending;
      this.sortByFieldChanged.emit(this.query);
    }
  }

  public isSelected(id: string | undefined): boolean {
    return this.selectedElementId === id;
  }
}
